
import { defineComponent, PropType } from 'vue'

import {
  CompiledPinnedReportT,
  Dataset,
  DataSetsT,
  HistoricalValues,
  ILoadingValue,
  IReportMetadata,
  IssueDistributionT,
  LoadingConditions,
  ReportMeta,
  ReportPageT
} from '~/types/reportTypes'
import { ReportLevel, ReportType } from '~/types/types'
import { dateRangeOptions } from '~/utils/date'
import { roundToSignificantNumber } from '~/utils/number'
import {
  formatCodeHealthTooltipY,
  getFilterType,
  getMaxDigitDistributionHistoricalValues
} from '~/utils/reports'
import { shortenLargeNumber } from '~/utils/string'
import { getColorShades } from '~/utils/ui'

interface IChartProps {
  axisOptions: Record<string, boolean>
  barOptions?: Record<string, number | boolean>
  class: string
  colors: Array<string>
  dataSets: DataSetsT
  tooltipOptions?: Record<string, unknown>
  labels: Array<string>
  type: string
  yAxisMax?: number
  yAxisMin: number
}

interface IEmptyChartProps {
  baseShade?: string
  chartColors?: Array<string>
  chartDataset?: Array<Dataset>
  chartType?: string
  count: number
  length?: number
  stacked?: boolean
}

/**
 * Component representing a pinned report widget
 */
export default defineComponent({
  name: 'PinnedChartReport',
  props: {
    allowPinningReports: {
      type: Boolean,
      required: true
    },
    compliancePassing: Boolean,
    datasets: {
      type: Array as PropType<DataSetsT>
    },
    error: Boolean,
    othersDatasetNames: Array,
    historicalValues: Object as PropType<HistoricalValues>,
    reportKey: {
      type: String as PropType<ReportPageT>,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    loadingValue: {
      type: Object as PropType<ILoadingValue>,
      required: true
    },
    metadata: {
      type: Object as PropType<IReportMetadata | null>
    },
    owner: {
      type: String,
      required: true
    },
    pinnedReports: {
      type: Array as PropType<Array<CompiledPinnedReportT>>,
      required: true
    },
    provider: {
      type: String,
      required: true
    },
    repositoryGroupSlug: String,
    repoName: String,
    reportSlot: {
      type: Number,
      required: true
    },
    value: Number,
    valueLabel: String
  },
  data() {
    return {
      dateRangeFilter: '',
      dateRangeOptions,
      revealReportControls: false,
      timeoutId: null as ReturnType<typeof setTimeout> | null,
      LoadingConditions
    }
  },
  created() {
    this.dateRangeFilter = this.getDateRangeFilter()
  },
  computed: {
    chartProps(): IChartProps | undefined {
      const labels = this.historicalValues?.labels ?? []

      const commonProps = {
        axisOptions: { xIsSeries: true },
        class: 'chart-tooltip-z-20',
        dataSets: this.datasets,
        labels,
        yAxisMin: 0
      }

      if (this.isComplianceReport) {
        const maxDigitHistoricalValues = Math.max(...(this.datasets?.[0].values as Array<number>))
        const exaggeratedMax = maxDigitHistoricalValues * 1.25
        const maxLineClip = roundToSignificantNumber(
          exaggeratedMax,
          exaggeratedMax.toString().length - 1
        )

        const colors = this.compliancePassing ? ['juniper-500'] : ['cherry-500']

        return {
          ...commonProps,
          colors,
          type: 'line',
          yAxisMax: maxLineClip
        } as IChartProps
      }

      if (this.isIssuesAutofixedReport) {
        return {
          ...commonProps,
          barOptions: { stacked: true },
          colors: ['juniper-500', 'juniper-100'],
          type: 'bar'
        } as IChartProps
      }

      if (this.isCodeHealthTrendReport) {
        return {
          ...commonProps,
          barOptions: { stacked: true },
          colors: ['cherry-500', 'juniper-500', 'robin-500'],
          tooltipOptions: {
            formatTooltipY: formatCodeHealthTooltipY
          },
          type: 'axis-mixed'
        } as IChartProps
      }

      // Remove the report key prefix from the `filter`
      // `issue-distribution-category` -> `category`
      const filterType = getFilterType(this.metadata?.filter) as IssueDistributionT
      const maxDigitHistoricalValues = getMaxDigitDistributionHistoricalValues(
        filterType,
        this.historicalValues as HistoricalValues
      )

      const maxBarClip = roundToSignificantNumber(
        maxDigitHistoricalValues,
        maxDigitHistoricalValues.toString().length - 1
      )

      const baseColorMap = {
        [ReportPageT.DISTRIBUTION]: '#1035ad',
        [ReportPageT.ISSUES_PREVENTED]: '#2eb78b'
      } as Record<ReportPageT, string>

      const colors = getColorShades(
        baseColorMap[this.reportKey as ReportPageT],
        this.datasets?.length as number
      )

      return {
        ...commonProps,
        barOptions: { stacked: true },
        colors,
        type: 'bar',
        yAxisMax: maxBarClip
      } as IChartProps
    },

    emptyChartProps(): IEmptyChartProps {
      const emptyCodeHealthChartDataSet = [
        { name: 'ACTIVE ISSUES', chartType: 'bar', values: [17, 19, 12, 9, 14, 5, 5, 4] },
        { name: 'RESOLVED ISSUES', chartType: 'bar', values: [-17, -15, -19, -11, -12, 0, -4, -2] },
        { name: 'NET NEW ISSUES THIS MONTH', chartType: 'line', values: [0, 4, -7, -2, 2, 5, 1, 2] }
      ]

      const propsMap = {
        [ReportPageT.OWASP_TOP_10]: {
          count: 1,
          chartType: 'line'
        },
        [ReportPageT.SANS_TOP_25]: {
          count: 1,
          chartType: 'line'
        },
        [ReportPageT.MISRA_C]: {
          count: 1,
          chartType: 'line'
        },
        [ReportPageT.DISTRIBUTION]: {
          chartType: 'bar',
          count: 5,
          stacked: true
        },
        [ReportPageT.ISSUES_PREVENTED]: {
          baseShade: '#2eb78b',
          chartType: 'bar',
          count: 5,
          stacked: true
        },
        [ReportPageT.ISSUES_AUTOFIXED]: {
          baseShade: '#33CB9A',
          count: 2,
          chartType: 'bar'
        },
        [ReportPageT.CODE_HEALTH_TREND]: {
          count: 3,
          length: 7,
          chartColors: ['cherry-500', 'juniper-500', 'robin-500'],
          chartDataset: emptyCodeHealthChartDataSet,
          stacked: true,
          chartType: 'axis-mixed'
        }
      } as Record<ReportPageT, IEmptyChartProps>

      return propsMap[this.reportKey]
    },

    helpText(): string {
      const { helpText } = ReportMeta[this.reportKey]
      return helpText ?? ''
    },

    isCodeHealthTrendReport(): boolean {
      return this.reportKey === ReportPageT.CODE_HEALTH_TREND
    },

    isComplianceReport(): boolean {
      return ReportMeta[this.reportKey].type === ReportType.Compliance
    },

    isReportWidgetDataFetch(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_WIDGET_DATA_FETCH
    },

    isReportGettingSwapped(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_SWAP
    },
    isIssuesAutofixedReport(): boolean {
      return this.reportKey === ReportPageT.ISSUES_AUTOFIXED
    },
    reportControlsWidth(): string {
      // Report controls width should be slightly more between `1024px` and `1280px`
      // since a part of the title gets clipped, and the controls go above it
      if (this.allowPinningReports) {
        return `w-60 ${
          this.metadata ? 'lg:w-72' : 'lg:w-64' // Distribution-based reports have filter text appended to the title, hence more width
        } xl:w-60`
      }

      return `w-52
        ${
          this.metadata ? 'lg:w-72 xl:w-52' : '' // Distribution-based reports have filter text appended to the title, hence more width
        }
      `
    },
    showChart(): boolean {
      return (this.historicalValues?.labels?.length ?? 0) >= 2 && (this.datasets?.length ?? 0) > 0
    },
    showChartLegend(): boolean {
      return !this.isComplianceReport && this.showChart
    },
    reportType(): string {
      const reportType = this.metadata?.filter || this.reportKey

      if (this.loadingValue.status || !reportType) {
        return ''
      }
      return reportType
    }
  },
  methods: {
    shortenLargeNumber,
    getDateRangeFilter(): string {
      const reportType = this.metadata?.filter || this.reportKey

      let cookieIdentifier = `${this.provider}_${this.owner}`

      if (this.level === ReportLevel.Repository) {
        cookieIdentifier += `_${this.repoName}`
      } else if (this.level === ReportLevel.RepositoryGroup) {
        cookieIdentifier += `_group_${this.repositoryGroupSlug}`
      }

      cookieIdentifier += `_${reportType}_date-range-filter_${this.reportSlot}`

      const validKeys = Object.keys(dateRangeOptions)
      const valueFromCookie = this.$cookies.get(cookieIdentifier)

      return validKeys.includes(valueFromCookie) ? valueFromCookie : validKeys[2]
    },

    hideReportControls(): void {
      this.timeoutId = setTimeout(() => {
        this.revealReportControls = false
      }, 1000)
    },

    navigateToReportPage(): void {
      const filterType = getFilterType(this.metadata?.filter)

      const atTeamLevel = this.level === ReportLevel.Owner
      const atRepoLevel = this.level === ReportLevel.Repository

      const reportPageUrl = {
        name: atRepoLevel
          ? `provider-owner-repo-reports-${this.reportKey}`
          : atTeamLevel
          ? `orgs-provider-owner-reports-${this.reportKey}`
          : `orgs-provider-owner-groups-groupSlug-reports-${this.reportKey}`,
        params: this.$route.params,
        query: {
          filter: [ReportPageT.DISTRIBUTION, ReportPageT.ISSUES_PREVENTED].includes(this.reportKey)
            ? filterType
            : undefined
        }
      }

      this.$router.push(reportPageUrl)
    },

    showReportControls(): void {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.revealReportControls = true
    },

    updateDateRangeHandler(newDateRange: string): void {
      this.dateRangeFilter = newDateRange

      this.$emit('update-report-controls', this.reportSlot, newDateRange)
    }
  }
})
